import "./dashboard-skeleton.css";

/** A single shimmer bar. */
function Bar({ w, h = 12, r = 6, style }: { w: number | string; h?: number; r?: number; style?: React.CSSProperties }) {
  return <span className="sk" style={{ width: w, height: h, borderRadius: r, ...style }} />;
}

/** KPI tile placeholder — mirrors ApiFlowCard: name, value, footer (delta +
 *  benchmark), and the sparkline area. */
function KpiCardSkeleton() {
  return (
    <div className="dl-card" aria-hidden="true">
      <div className="dl-cname"><Bar w={112} h={11} /></div>
      <div className="dl-cval num"><Bar w={96} h={26} r={7} /></div>
      <div className="dl-cfoot">
        <Bar w={54} h={12} />
        <Bar w={72} h={16} r={8} />
      </div>
      <div className="dl-spark"><Bar w="100%" h={22} r={4} /></div>
    </div>
  );
}

/** One "More metrics" row placeholder — mirrors ApiCompactMetric. */
function CompactMetricSkeleton() {
  return (
    <div className="cmpt" aria-hidden="true">
      <span className="cmpt-name"><Bar w={140} h={11} /></span>
      <span className="cmpt-right">
        <Bar w={52} h={13} />
        <Bar w={34} h={12} />
      </span>
    </div>
  );
}

/** By-Branch table placeholder — mirrors the toolbar + `clintbl` header/rows so
 *  the real table drops in without a layout shift. */
function BranchTableSkeleton({ cols = 6, rows = 8 }: { cols?: number; rows?: number }) {
  const colArr = Array.from({ length: cols });
  const rowArr = Array.from({ length: rows });
  return (
    <div className="pp-card" style={{ padding: 0, overflow: "visible" }} aria-hidden="true">
      <div className="tbl-toolbar">
        <Bar w={150} h={13} />
        <div style={{ flex: 1 }} />
        <Bar w={150} h={30} r={8} />
        <Bar w={92} h={30} r={8} style={{ marginLeft: 8 }} />
        <Bar w={92} h={30} r={8} style={{ marginLeft: 8 }} />
      </div>
      <div style={{ overflowX: "auto" }}>
        <table className="clintbl">
          <thead>
            <tr>
              <th><Bar w={60} h={11} /></th>
              {colArr.map((_, i) => (
                <th key={i} className="num"><Bar w={54} h={11} /></th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowArr.map((_, r) => (
              <tr key={r}>
                <td className="who"><Bar w={130 + ((r * 23) % 70)} h={11} /></td>
                {colArr.map((_, c) => (
                  <td key={c} className="num"><Bar w={40} h={11} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Snapshot tile placeholder — mirrors ApiSnapCard (dot + name, value, footer). */
function SnapCardSkeleton() {
  return (
    <div className="dl-card snap" aria-hidden="true">
      <div className="dl-cname"><Bar w={7} h={7} r={4} style={{ marginTop: 5 }} /><Bar w={118} h={11} /></div>
      <div className="dl-cval num"><Bar w={88} h={26} r={7} /></div>
      <div className="snap-foot"><Bar w={64} h={11} /></div>
    </div>
  );
}

/** Loading placeholder for the Current State grid — the same `dl-grid` of
 *  snapshot tiles the live page renders, so there is no layout jump. */
export function CurrentStateSkeleton({ tiles = 9 }: { tiles?: number }) {
  return (
    <div className="dl-grid" style={{ marginTop: 10 }} aria-hidden="true">
      {Array.from({ length: tiles }).map((_, i) => (
        <SnapCardSkeleton key={i} />
      ))}
    </div>
  );
}

/**
 * Full loading placeholder for everything driven by the Dashboard Metrics API:
 * the KPI cards, the More-metrics grid, and the By-Branch table. Structured to
 * match the live Operating View so swapping in real data causes no layout jump.
 */
export function DashboardMetricsSkeleton({
  kpis = 4,
  more = 8,
  showTable = true,
}: {
  kpis?: number;
  more?: number;
  showTable?: boolean;
}) {
  return (
    <>
      <div className="pp-kpis">
        {Array.from({ length: kpis }).map((_, i) => (
          <KpiCardSkeleton key={i} />
        ))}
      </div>
      {more > 0 && (
        <div className="pp-more">
          <div className="pp-more-lbl"><Bar w={92} h={10} /></div>
          <div className="pp-more-grid">
            {Array.from({ length: more }).map((_, i) => (
              <CompactMetricSkeleton key={i} />
            ))}
          </div>
        </div>
      )}
      {showTable && <BranchTableSkeleton />}
    </>
  );
}
